import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Ask the SERVER to recompute today's health score after a task event
 * (food logged, water added, steps synced).
 *
 * IMPORTANT: there is NO client-side score computation anywhere. The server
 * (compute-health-score) remains the single source of truth; the client only
 * invokes it and then re-reads health_scores.
 *
 * Debounced so a burst of logs (e.g. a multi-item meal) fires one recompute.
 */
const DEBOUNCE_MS = 2500;

let timer: ReturnType<typeof setTimeout> | null = null;
let pending: { userId: string; qc?: QueryClient } | null = null;

async function run(userId: string, qc?: QueryClient) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    await supabase.functions.invoke('compute-health-score', {
      body: { user_id: userId },
    });
    qc?.invalidateQueries({ queryKey: ['healthScore', userId] });
  } catch {
    // non-fatal — never block a log on the score
  }
}

export function triggerScoreRecompute(userId?: string | null, qc?: QueryClient) {
  if (!userId) return;
  pending = { userId, qc };
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    const p = pending;
    pending = null;
    if (p) void run(p.userId, p.qc);
  }, DEBOUNCE_MS);
}
